import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  UserRound,
  Users,
  Swords,
  CalendarDays,
  Calendar,
  Map,
  Settings,
  LogOut,
  X,
  Globe,
  ChevronDown,
  Activity,
  ClipboardList,
  Star,
  Headphones,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { useAuth, isOperator, isAdmin } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { positionLabel, useMyPositions } from '@/lib/myPositions'
import { type Language } from '@/i18n/translations'

const ROLE_COLOR: Record<string, string> = {
  owner:     'text-yellow-400',
  admin:     'text-purple-400',
  moderator: 'text-blue-400',
  member:    'text-muted-foreground',
}

interface SidebarProps {
  open: boolean
  onClose: () => void
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { user, logout } = useAuth()
  const { lang, setLang, T } = useLanguage()
  const { positions } = useMyPositions()
  const cardRef = useRef<HTMLDivElement>(null)
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50, opacity: 0 })
  const [pendingApplications, setPendingApplications] = useState(0)

  // Poll the pending-application count so the badge stays honest while the
  // dashboard is left open. Operators only — the endpoint 403s for members.
  const canSeeApplications = isOperator(user)
  useEffect(() => {
    if (!canSeeApplications) return
    let cancelled = false
    const fetchCount = async () => {
      try {
        const res = await fetch('/api/applications/pending-count', { credentials: 'include' })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setPendingApplications(data.count ?? 0)
      } catch { /* offline — keep the last known count */ }
    }
    fetchCount()
    const id = setInterval(fetchCount, 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [canSeeApplications])

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setSpotlight({ x, y, opacity: 1 })
  }

  const handleMouseLeave = () => setSpotlight(s => ({ ...s, opacity: 0 }))

  const navSections: {
    title?: string
    operatorOnly?: boolean
    adminOnly?: boolean
    items: { to: string; label: string; icon: typeof LayoutDashboard; operatorOnly?: boolean; adminOnly?: boolean; badge?: number; isNew?: boolean }[]
  }[] = [
    {
      items: [
        { to: '/', label: T.nav.dashboard, icon: LayoutDashboard },
        { to: '/profile', label: T.nav.profile, icon: UserRound, isNew: true },
      ],
    },
    {
      title: T.nav.competitive,
      items: [
        { to: '/members', label: T.nav.members, icon: Users },
        { to: '/matches', label: T.nav.matches, icon: Swords },
        { to: '/events', label: T.nav.events, icon: CalendarDays },
        { to: '/activity', label: T.nav.activity, icon: Activity },
        { to: '/calendar', label: T.nav.calendar, icon: Calendar },
        { to: '/strategy', label: T.nav.strategy, icon: Map },
      ],
    },
    {
      title: T.nav.management,
      operatorOnly: true,
      items: [
        { to: '/applications', label: T.nav.applications, icon: ClipboardList, badge: pendingApplications },
        { to: '/supporters', label: T.nav.supporters, icon: Star },
        { to: '/briefing', label: T.nav.briefing, icon: Headphones },
        { to: '/settings', label: T.nav.settings, icon: Settings, adminOnly: true },
      ],
    },
  ]

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-30 flex h-screen w-64 flex-col border-r border-white/10
        bg-black/35 text-foreground backdrop-blur-md
        transition-transform duration-300 ease-in-out
        md:relative md:translate-x-0
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      {/* Marka bloğu halka açık sitenin üst çubuğundakinin aynısı: 40px logo,
          display yüzünde büyük harf ad, altında aynı slogan. Yükseklik de
          aynı (68px), böylece sidebar'ın alt kenarı sayfa başlığının alt
          kenarıyla tek hat üstünde buluşur. */}
      <div className="flex h-[68px] shrink-0 items-center gap-3 border-b border-white/10 px-5">
        <img src="/tll_logo.png" alt="" className="h-10 w-10 shrink-0 rounded-md object-contain" />
        <div className="min-w-0">
          <p className="font-display text-sm font-bold uppercase leading-none tracking-tight">
            {T.join.brand}
          </p>
          <p className="mt-1 truncate text-[10px] uppercase tracking-wider text-muted-foreground">
            {T.join.tagline}
          </p>
        </div>
        {/* Close button - mobile only */}
        <button
          onClick={onClose}
          className="ml-auto shrink-0 text-muted-foreground transition-colors hover:text-foreground md:hidden"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {navSections.filter(s => {
          if (s.operatorOnly && !isOperator(user)) return false
          if (s.adminOnly && !isAdmin(user)) return false
          return true
        }).map((section, sIdx) => (
          <div key={sIdx} className={sIdx > 0 ? 'mt-5' : ''}>
            {section.title && (
              <p className="mb-2 flex items-center gap-2 px-3 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/70">
                <span aria-hidden className="h-px w-3 shrink-0 bg-primary/70" />
                {section.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.filter(item => {
                if (item.operatorOnly && !isOperator(user)) return false
                if (item.adminOnly && !isAdmin(user)) return false
                return true
              }).map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-sm py-2 pl-3.5 pr-3 font-display text-[13px] font-semibold tracking-tight transition-colors ${
                        isActive
                          ? 'bg-white/[0.07] text-foreground'
                          : 'text-foreground/65 hover:bg-white/[0.04] hover:text-foreground'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* Etkin satırın işareti soldaki kızıl çubuk. Nokta
                            yerine çubuk, çünkü rozeti olan satırda nokta
                            basılamıyordu — işaret satırın hangi tarafta
                            olduğuna bakmadan hep aynı yerde durmalı. */}
                        {isActive && (
                          <span aria-hidden className="absolute inset-y-1 left-0 w-[2px] rounded-full bg-primary" />
                        )}
                        <item.icon
                          className={`h-4 w-4 shrink-0 transition-colors ${
                            isActive
                              ? 'text-primary'
                              : 'text-foreground/45 group-hover:text-foreground/80'
                          }`}
                        />
                        {item.label}
                        {item.badge ? (
                          <span className="ml-auto rounded-sm bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary-foreground">
                            {item.badge}
                          </span>
                        ) : null}
                        {item.isNew && (
                          <span className="ml-auto rounded-sm bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary-foreground motion-safe:animate-pulse">
                            {T.nav.newBadge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Language switcher */}
      <div className="px-4 pb-2">
        <div className="flex items-center gap-2 rounded-sm border border-white/10 bg-white/[0.03] px-2.5 py-1.5 transition-colors hover:bg-white/[0.06]">
          <Globe className="h-3 w-3 shrink-0 text-muted-foreground/50" />
          <span className="flex-1 text-[10px] uppercase tracking-wider text-muted-foreground/50">{T.language.select}</span>
          <div className="relative">
            <select
              value={lang}
              onChange={e => setLang(e.target.value as Language)}
              className="cursor-pointer appearance-none rounded-sm border border-white/12 bg-white/[0.06] py-0.5 pl-2 pr-5 text-[10px] font-bold tracking-wider text-foreground/80 transition-colors hover:bg-white/10 focus:outline-none"
            >
              <option value="en">EN</option>
              <option value="tr">TR</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-muted-foreground/50" />
          </div>
        </div>
      </div>

      {/* User card */}
      {user && (
        <>
          <Separator className="bg-white/10" />
          <div className="p-3">
            <div
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="relative flex items-center gap-3 overflow-hidden rounded-sm border border-white/10 bg-white/[0.04] px-3 py-2.5"
          >
            {/* Spotlight */}
            <div
              className="pointer-events-none absolute inset-0 transition-opacity duration-300"
              style={{
                opacity: spotlight.opacity,
                background: `radial-gradient(200px circle at ${spotlight.x}% ${spotlight.y}%, rgba(255,255,255,0.07), transparent 70%)`,
              }}
            />
              {/* Kartın kendisi profilin kapısıdır; çıkış düğmesi bu bağlantının dışında. */}
              <NavLink
                to="/profile"
                onClick={onClose}
                title={T.nav.profile}
                className="relative flex min-w-0 flex-1 items-center gap-3"
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span aria-hidden className="absolute -inset-y-2.5 -left-3 w-[2px] bg-primary" />
                    )}
                    <img
                      src={user.avatar}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-white/10"
                      onError={e => { (e.target as HTMLImageElement).src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-[13px] font-semibold leading-tight tracking-tight">{user.name}</p>
                      {/* Simgeler sığmazsa alt satıra iner; rol hep görünür kalır. */}
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <p className={`text-[10px] font-bold uppercase tracking-[0.14em] ${ROLE_COLOR[user.role]}`}>
                          {T.roles[user.role as keyof typeof T.roles] ?? user.role}
                        </p>
                        {positions.length > 0 && (
                          <span className="flex items-center gap-1">
                            {positions.map(p => p.emoji
                              ? <EmojiGlyph key={p.id} emoji={p.emoji} size={14} title={positionLabel(p)} />
                              : (
                                <span key={p.id} title={positionLabel(p)} className="text-[9px] text-muted-foreground">
                                  {p.name.slice(0, 3)}
                                </span>
                              ))}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </NavLink>
              <button
                onClick={logout}
                title={T.nav.signOut}
                className="relative shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              >
                <LogOut className="h-4 w-4" />
              </button>
          </div>
          </div>
        </>
      )}
    </aside>
  )
}
